"use client";

import { useState } from "react";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { countCitations } from "@/lib/tool-kit/reference-checkers/citation-count";

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-center">
      <p className="font-semibold text-2xl text-foreground">{value}</p>
      <p className="mt-1 text-muted-foreground text-sm">{label}</p>
    </div>
  );
}

/** English strings. A translated content file overrides these through `ui`. */
export const CITATION_COUNTER_STRINGS = {
  textToCountCitationsIn: "Text to count citations in",
  pasteYourAcademicTextHere:
    "Paste your academic text here to count citations...",
  totalCitations: "Total Citations",
  uniqueCitations: "Unique Citations",
  citationsPer100Words: "Citations per 100 Words",
  citationsByType: "Citations by Type",
  detectedCitations: "Detected Citations",
} as const;

export default function CitationCounterWidget() {
  const [text, setText] = useState("");

  const stats = countCitations(text);

  const strings = useToolUi(CITATION_COUNTER_STRINGS);

  const fmt = useToolNumber();

  return (
    <>
      {/* Input */}
      <textarea
        aria-label={strings.textToCountCitationsIn}
        className="min-h-[200px] w-full resize-y rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
        onChange={(e) => setText(e.target.value)}
        placeholder={strings.pasteYourAcademicTextHere}
        value={text}
      />
      {/* Stats Grid */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
        <StatCard label={strings.totalCitations} value={fmt(stats.total)} />
        <StatCard label={strings.uniqueCitations} value={fmt(stats.unique)} />
        <StatCard label={strings.citationsPer100Words} value={stats.density} />
      </div>
      {/* Citation Type Breakdown */}
      {stats.byType.length > 0 && (
        <section className="space-y-3">
          <h2 className="font-semibold text-foreground text-lg">
            {strings.citationsByType}
          </h2>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {stats.byType.map((item) => (
              <div
                className="rounded-lg border border-border bg-card px-4 py-3 text-center"
                key={item.type}
              >
                <p className="font-semibold text-foreground text-lg">
                  {fmt(item.count)}
                </p>
                <p className="text-muted-foreground text-xs">{item.type}</p>
              </div>
            ))}
          </div>
        </section>
      )}
      {/* Detected Citations List */}
      {stats.citations.length > 0 && (
        <section className="space-y-3">
          <h2 className="font-semibold text-foreground text-lg">
            {strings.detectedCitations}
          </h2>
          <div className="max-h-64 space-y-1 overflow-y-auto rounded-lg border border-border bg-muted/30 p-4">
            {stats.citations.map((c, i) => (
              <div
                className="flex items-center justify-between gap-4 border-border border-b py-1.5 last:border-b-0"
                key={`${c.text}-${i}`}
              >
                <code className="text-foreground text-sm">{c.text}</code>
                <span className="shrink-0 text-muted-foreground text-xs">
                  {c.type}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}
    </>
  );
}
